import { forwardRef, useEffect, useState } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { PARTS, CATEGORIES, isMultiSelect } from '../../data/parts';
import styles from './Share.module.css';

// 海报模板配置
const TEMPLATES = {
    wild: {
        id: 'wild',
        name: { zh: '极速狂野', en: 'Wild Speed', de: 'Wilde Geschwindigkeit' },
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1810 50%, #1a1a1a 100%)',
        accent: '#FF6B00',
        textColor: '#FFFFFF'
    },
    classic: {
        id: 'classic',
        name: { zh: '经典传承', en: 'Classic Heritage', de: 'Klassisches Erbe' },
        background: 'linear-gradient(135deg, #2C2416 0%, #1A1A1A 100%)',
        accent: '#D4AF37',
        textColor: '#F5F5DC'
    },
    tech: {
        id: 'tech',
        name: { zh: '科技未来', en: 'Tech Future', de: 'Tech Zukunft' },
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3a 50%, #0a0a1a 100%)',
        accent: '#00D4FF',
        textColor: '#E0E0E0'
    }
};

/**
 * 海报预览组件
 * 用于渲染海报内容，供 html2canvas 截图使用
 *
 * 支持两种数据源：
 * 1. 外部传入数据 (externalConfigData, externalCoverImage 等) - 配置库/详情页分享
 * 2. ConfigContext 数据 - 配置器分享
 */
const PosterPreview = forwardRef(function PosterPreview({
    template = 'wild',
    qrCodeUrl,
    shareLink,
    // 外部传入的配置数据
    externalConfigData = null,
    externalCoverImage = null,
    externalTotalPrice = null,
    externalConfigTitle = null,
    // 传奇分享数据
    isLegendShare = false,
    legendTitle = null,
    legendImage = null,
    // 图片准备完成回调
    onImageReady = null,
}, ref) {
    const { config, totalPrice: contextTotalPrice, getSelectedPart } = useConfig();
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const [bikeImageLoaded, setBikeImageLoaded] = useState(false);
    const [bikeImageBase64, setBikeImageBase64] = useState(null);
    const [imageConversionStatus, setImageConversionStatus] = useState('idle'); // idle, converting, success, failed

    const currentTemplate = TEMPLATES[template] || TEMPLATES.wild;

    // 判断是否使用外部数据
    const useExternalData = !!(externalConfigData || externalCoverImage);

    // 获取摩托车图片源 URL
    // 优先级：传奇图片 > 外部封面图 > ConfigContext 的图片
    const getBikeImageSrc = () => {
        if (isLegendShare && legendImage) {
            return legendImage;
        }
        if (externalCoverImage) {
            return externalCoverImage;
        }
        const bodyPart = getSelectedPart('body');
        return bodyPart?.image || '/assets/360/2026-fat-boy-m85-motorcycle/2026-fat-boy-m85-motorcycle-01.jpg';
    };
    const bikeImageSrc = getBikeImageSrc();

    // 通知父组件图片已准备好
    useEffect(() => {
        if (bikeImageLoaded && imageConversionStatus !== 'converting' && onImageReady) {
            onImageReady(imageConversionStatus === 'success');
        }
    }, [bikeImageLoaded, imageConversionStatus, onImageReady]);

    // 将外部图片转换为 base64（解决跨域问题）
    useEffect(() => {
        const convertToBase64 = async (url) => {
            // 如果是本地图片或已经是 base64，直接使用
            if (!url || url.startsWith('/') || url.startsWith('data:')) {
                setBikeImageBase64(url);
                setBikeImageLoaded(true);
                setImageConversionStatus('success');
                console.log('[PosterPreview] Using local/base64 image directly');
                return;
            }

            setImageConversionStatus('converting');
            console.log('[PosterPreview] Converting external image:', url);

            // 方法1: 尝试使用 Image + Canvas（支持 CORS 的情况）
            const tryImageCanvas = () => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        try {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.naturalWidth;
                            canvas.height = img.naturalHeight;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                            resolve(dataUrl);
                        } catch (e) {
                            reject(e);
                        }
                    };
                    img.onerror = (e) => reject(new Error('Image load failed'));
                    // 添加时间戳避免缓存问题
                    img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
                });
            };

            // 方法2: 尝试使用 fetch
            const tryFetch = async () => {
                const response = await fetch(url, { mode: 'cors' });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            };

            // 方法3: 使用后端代理（通过 API 获取图片）
            const tryBackendProxy = async () => {
                // 构建代理 URL（使用已有的 /api/proxy/texture 端点）
                const apiBase = import.meta.env.VITE_API_BASE_URL || '';
                const proxyUrl = `${apiBase}/api/proxy/texture?url=${encodeURIComponent(url)}`;
                const response = await fetch(proxyUrl);
                if (!response.ok) throw new Error(`Proxy HTTP ${response.status}`);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            };

            // 依次尝试三种方法
            try {
                const base64 = await tryImageCanvas();
                setBikeImageBase64(base64);
                setBikeImageLoaded(true);
                setImageConversionStatus('success');
                console.log('[PosterPreview] Image converted via canvas');
            } catch (e1) {
                console.warn('[PosterPreview] Canvas method failed:', e1.message);
                try {
                    const base64 = await tryFetch();
                    setBikeImageBase64(base64);
                    setBikeImageLoaded(true);
                    setImageConversionStatus('success');
                    console.log('[PosterPreview] Image converted via fetch');
                } catch (e2) {
                    console.warn('[PosterPreview] Fetch method failed:', e2.message);
                    try {
                        const base64 = await tryBackendProxy();
                        setBikeImageBase64(base64);
                        setBikeImageLoaded(true);
                        setImageConversionStatus('success');
                        console.log('[PosterPreview] Image converted via backend proxy');
                    } catch (e3) {
                        console.error('[PosterPreview] All methods failed:', e3.message);
                        // 都失败了，直接使用原始 URL（可能在预览中可以显示，但海报生成可能有问题）
                        setBikeImageBase64(url);
                        setBikeImageLoaded(true);
                        setImageConversionStatus('failed');
                    }
                }
            }
        };

        if (bikeImageSrc) {
            setBikeImageLoaded(false);
            setImageConversionStatus('idle');
            convertToBase64(bikeImageSrc);
        }
    }, [bikeImageSrc]);

    // 最终使用的图片（优先使用 base64）
    const bikeImage = bikeImageBase64 || bikeImageSrc;

    // 获取总价
    const displayTotalPrice = externalTotalPrice ?? contextTotalPrice;

    // 获取所有选中配件的名称列表
    const getSelectedPartsNames = () => {
        const parts = [];

        // 如果有外部配置数据，使用外部数据
        if (useExternalData && externalConfigData) {
            // 处理 body（车身颜色）
            if (externalConfigData.body) {
                const bodyPart = PARTS.body?.find(p => p.id === externalConfigData.body);
                if (bodyPart) {
                    parts.push(t(bodyPart.nameKey) || bodyPart.id);
                }
            }
            // 处理 trim（装饰风格）
            if (externalConfigData.trim) {
                const trimPart = PARTS.trim?.find(p => p.id === externalConfigData.trim);
                if (trimPart) {
                    parts.push(t(trimPart.nameKey) || trimPart.id);
                }
            }
            // 处理 performance（性能配件）
            if (externalConfigData.performance && externalConfigData.performance !== 'none') {
                const perfItems = Array.isArray(externalConfigData.performance)
                    ? externalConfigData.performance
                    : [externalConfigData.performance];
                perfItems.forEach(perfId => {
                    if (perfId && perfId !== 'none') {
                        const perfPart = PARTS.performance?.find(p => p.id === perfId);
                        if (perfPart) {
                            parts.push(t(perfPart.nameKey) || perfPart.id);
                        }
                    }
                });
            }
            // 处理 comfort（舒适配件）
            if (externalConfigData.comfort?.length > 0) {
                externalConfigData.comfort.forEach(comfortId => {
                    const comfortPart = PARTS.comfort?.find(p => p.id === comfortId);
                    if (comfortPart) {
                        parts.push(t(comfortPart.nameKey) || comfortPart.id);
                    }
                });
            }
            // 处理 style（风格配件）
            if (externalConfigData.style?.length > 0) {
                externalConfigData.style.forEach(styleId => {
                    const stylePart = PARTS.style?.find(p => p.id === styleId);
                    if (stylePart) {
                        parts.push(t(stylePart.nameKey) || stylePart.id);
                    }
                });
            }
            return parts;
        }

        // 使用 ConfigContext 的数据
        CATEGORIES.forEach(category => {
            const categoryId = category.id;

            if (isMultiSelect(categoryId)) {
                const selectedParts = getSelectedPart(categoryId);
                if (Array.isArray(selectedParts)) {
                    selectedParts.forEach(part => {
                        if (part) {
                            parts.push(t(part.nameKey) || part.nameKey);
                        }
                    });
                }
            } else {
                const part = getSelectedPart(categoryId);
                if (part && part.id !== 'none' && part.id !== 'chrome') {
                    parts.push(t(part.nameKey) || part.nameKey);
                }
            }
        });

        return parts;
    };

    const selectedPartsNames = getSelectedPartsNames();
    const displayName = user?.nickname || user?.email?.split('@')[0] || 'Rider';

    return (
        <div
            ref={ref}
            className={styles.posterContainer}
            style={{
                background: currentTemplate.background,
                '--accent-color': currentTemplate.accent,
                '--text-color': currentTemplate.textColor
            }}
        >
            {/* 顶部装饰线 */}
            <div className={styles.topDecoration}>
                <div className={styles.decorLine} style={{ backgroundColor: currentTemplate.accent }} />
                <span className={styles.brandName}>HARLEY-DAVIDSON</span>
                <div className={styles.decorLine} style={{ backgroundColor: currentTemplate.accent }} />
            </div>

            {/* 摩托车图片区域 */}
            <div className={styles.bikeImageWrapper}>
                <img
                    src={bikeImage}
                    alt="Fat Boy Configuration"
                    className={styles.bikeImage}
                    crossOrigin="anonymous"
                    onLoad={() => setBikeImageLoaded(true)}
                />
                {/* 模板特效叠加层 */}
                {template === 'wild' && <div className={styles.fireOverlay} />}
                {template === 'tech' && <div className={styles.techOverlay} />}
            </div>

            {/* 标题 */}
            <div className={styles.posterTitle}>
                <h2 style={{ color: currentTemplate.accent }}>MY CUSTOM FAT BOY</h2>
                <p className={styles.subtitle}>2026 FAT BOY® 114</p>
            </div>

            {/* 配置详情 */}
            <div className={styles.configDetails}>
                <h3 className={styles.configTitle}>
                    {{ zh: '我的配置', en: 'My Configuration', de: 'Meine Konfiguration' }[language] || 'My Configuration'}
                </h3>
                <ul className={styles.configList}>
                    {selectedPartsNames.length > 0 ? (
                        selectedPartsNames.slice(0, 5).map((name, index) => (
                            <li key={index} className={styles.configItem}>
                                <span
                                    className={styles.bullet}
                                    style={{ backgroundColor: currentTemplate.accent }}
                                />
                                {name}
                            </li>
                        ))
                    ) : (
                        <li className={styles.configItem}>
                            <span
                                className={styles.bullet}
                                style={{ backgroundColor: currentTemplate.accent }}
                            />
                            {{ zh: '标准配置', en: 'Standard Configuration', de: 'Standardkonfiguration' }[language] || 'Standard Configuration'}
                        </li>
                    )}
                    {selectedPartsNames.length > 5 && (
                        <li className={styles.configItem} style={{ opacity: 0.7 }}>
                            +{selectedPartsNames.length - 5} {{ zh: '更多配件', en: 'more items', de: 'weitere Artikel' }[language] || 'more items'}
                        </li>
                    )}
                </ul>
            </div>

            {/* 价格 */}
            <div className={styles.priceSection}>
                <span className={styles.priceLabel}>
                    {{ zh: '配置总价', en: 'Total Price', de: 'Gesamtpreis' }[language] || 'Total Price'}
                </span>
                <span
                    className={styles.priceValue}
                    style={{ color: currentTemplate.accent }}
                >
                    ¥{displayTotalPrice.toLocaleString()}
                </span>
            </div>

            {/* 二维码区域 */}
            <div className={styles.qrSection}>
                <div className={styles.qrWrapper}>
                    {qrCodeUrl ? (
                        <img
                            src={qrCodeUrl}
                            alt="QR Code"
                            className={styles.qrCode}
                        />
                    ) : (
                        <div className={styles.qrPlaceholder}>
                            <span>QR</span>
                        </div>
                    )}
                </div>
                <div className={styles.qrInfo}>
                    <p className={styles.scanText}>
                        {{ zh: '扫码查看我的配置', en: 'Scan to view my config', de: 'Scannen für meine Konfiguration' }[language] || 'Scan to view my config'}
                    </p>
                    <p className={styles.userName}>@{displayName}</p>
                </div>
            </div>

            {/* 底部品牌 */}
            <div className={styles.bottomBrand}>
                <div className={styles.decorLine} style={{ backgroundColor: currentTemplate.accent }} />
                <span className={styles.slogan}>
                    {{ zh: '定制你的传奇', en: 'BUILD YOUR LEGEND', de: 'BAUE DEINE LEGENDE' }[language] || 'BUILD YOUR LEGEND'}
                </span>
                <div className={styles.decorLine} style={{ backgroundColor: currentTemplate.accent }} />
            </div>
        </div>
    );
});

export default PosterPreview;
export { TEMPLATES };
